import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getUnifiedSuggestions,
  resolveSuggestionArea,
  UnifiedSuggestItem,
} from "../../apis/search";
import { resolveAreaByText, SelectedLocation } from "./resolveByText";
import { generateSessionToken } from "./sessionToken";

/** Cities we cover, grouped under their country in server-provided order. */
export type CityGroup = {
  country: string;
  cities: UnifiedSuggestItem[];
};

// The covered tier reads a cached facet, so it can be eager. The uncovered tier
// costs a Google request per call, so it lags behind and starts later.
const COVERED_DEBOUNCE_MS = 150;
const UNCOVERED_DEBOUNCE_MS = 500;
const COVERED_MIN_LENGTH = 1;
const UNCOVERED_MIN_LENGTH = 3;

type Options = {
  /** Bias ambiguous city names towards this point. */
  latitude?: number;
  longitude?: number;
  /** Skip fetching entirely, e.g. while the picker is closed. */
  enabled?: boolean;
};

/**
 * Two-tier city autosuggest. Tier one is cities we have mapped venues in, grouped
 * by country; tier two is everything else Google knows about, offered as places the
 * user could help map. They load independently so the cheap tier renders first.
 */
export function useCitySuggest(term: string, options: Options = {}) {
  const { latitude, longitude, enabled = true } = options;

  const [covered, setCovered] = useState<UnifiedSuggestItem[]>([]);
  const [needsHelp, setNeedsHelp] = useState<UnifiedSuggestItem[]>([]);
  const [isLoadingCovered, setIsLoadingCovered] = useState(false);
  const [isLoadingNeedsHelp, setIsLoadingNeedsHelp] = useState(false);
  /**
   * The term `covered` currently answers, `""` before anything has been asked.
   * Callers need it to tell "we found nothing" from "we have not looked yet":
   * the results for the previous keystroke are still in state while the next
   * one is in flight, so emptiness alone is not an answer.
   */
  const [searchedTerm, setSearchedTerm] = useState("");

  const sessionTokenRef = useRef<string>("");

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = "";
  }, []);

  const trimmed = term.trim();

  const coveredRequestRef = useRef(0);
  useEffect(() => {
    if (!enabled || trimmed.length < COVERED_MIN_LENGTH) {
      coveredRequestRef.current += 1;
      setCovered([]);
      setSearchedTerm("");
      setIsLoadingCovered(false);
      return;
    }

    // Set here rather than inside the timer: the debounce is part of the wait
    // as far as anyone watching this flag is concerned, and leaving it false
    // for the first 150ms of every keystroke let callers render a settled
    // "nothing found" state over a search that had not started yet.
    setIsLoadingCovered(true);

    const timer = setTimeout(async () => {
      const requestId = ++coveredRequestRef.current;
      try {
        const list = await getUnifiedSuggestions(trimmed, {
          latitude,
          longitude,
          scope: "cities",
          tiers: "covered",
        });
        if (requestId !== coveredRequestRef.current) return;
        setCovered(list);
      } catch {
        if (requestId !== coveredRequestRef.current) return;
        setCovered([]);
      } finally {
        if (requestId === coveredRequestRef.current) {
          setSearchedTerm(trimmed);
          setIsLoadingCovered(false);
        }
      }
    }, COVERED_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmed, latitude, longitude, enabled]);

  const needsHelpRequestRef = useRef(0);
  useEffect(() => {
    if (!enabled || trimmed.length < UNCOVERED_MIN_LENGTH) {
      needsHelpRequestRef.current += 1;
      setNeedsHelp([]);
      setIsLoadingNeedsHelp(false);
      return;
    }

    // See the covered tier above — the debounce counts as loading.
    setIsLoadingNeedsHelp(true);

    const timer = setTimeout(async () => {
      const requestId = ++needsHelpRequestRef.current;
      try {
        const list = await getUnifiedSuggestions(trimmed, {
          latitude,
          longitude,
          sessionToken: ensureSessionToken(),
          scope: "cities",
          tiers: "uncovered",
        });
        if (requestId !== needsHelpRequestRef.current) return;
        setNeedsHelp(list);
      } catch {
        if (requestId !== needsHelpRequestRef.current) return;
        setNeedsHelp([]);
      } finally {
        if (requestId === needsHelpRequestRef.current) {
          setIsLoadingNeedsHelp(false);
        }
      }
    }, UNCOVERED_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmed, latitude, longitude, enabled, ensureSessionToken]);

  // The backend already ordered countries and the cities inside them, so this only
  // has to fold the flat list into runs without disturbing it.
  const groups = useMemo<CityGroup[]>(() => {
    const out: CityGroup[] = [];
    for (const city of covered) {
      const country = city.data?.country ?? "";
      const last = out[out.length - 1];
      if (last && last.country === country) {
        last.cities.push(city);
      } else {
        out.push({ country, cities: [city] });
      }
    }
    return out;
  }, [covered]);

  /**
   * Resolve a city to coordinates. Covered cities come from our index with no
   * place_id, so they resolve by text; uncovered ones close the Google session.
   *
   * Both routes go through `resolve-suggestion-area`, which 404s outright when
   * the Google integration is off — so both fall back to the legacy geocode
   * rather than only the tier that depends on Google.
   */
  const selectCity = useCallback(
    async (item: UnifiedSuggestItem): Promise<SelectedLocation | null> => {
      const action = item.action;
      const placeId =
        action?.type === "SELECT_CITY" ? action.placeId : (item.data?.place_id ?? null);
      const query =
        action?.type === "SELECT_CITY"
          ? action.query
          : [item.displayText, item.subtitle].filter(Boolean).join(", ");

      const area = await resolveSuggestionArea(
        placeId
          ? { placeId, sessionToken: sessionTokenRef.current || null }
          : { query, latitude, longitude },
      );
      resetSessionToken();

      if (!area) {
        const fallback = await resolveAreaByText(query, latitude, longitude);
        if (!fallback) return null;
        // The geocoder labels by address; the row the user tapped is the better
        // short label, and it's the one they'll expect to see on the map.
        return {
          ...fallback,
          simpleText: item.displayText || fallback.simpleText,
        };
      }

      return {
        text: area.address || query,
        simpleText: area.name || item.displayText,
        latitude: area.lat,
        longitude: area.lng,
        placeId: area.place_id,
      };
    },
    [latitude, longitude, resetSessionToken],
  );

  return {
    /** Covered cities grouped by country. */
    groups,
    /** Flat covered list, if grouping isn't wanted. */
    covered,
    /** Uncovered cities, i.e. "cities that need your help". */
    needsHelp,
    isLoadingCovered,
    isLoadingNeedsHelp,
    searchedTerm,
    selectCity,
    resetSessionToken,
  };
}
